import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Subscription, of } from 'rxjs';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { ToolsService } from 'src/app/service/tools/tools.service';
import { SelectedDataModel, InventoryModel, TransferModelData } from './tool-inventory-modal.model';

@Injectable({
  providedIn: 'root',
})
export class ToolInventoryModalService {
  private selectedInventoryItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedInventoryItems = this.selectedInventoryItemsSource.asObservable();

  private inventoryModalIsOpenSource = new BehaviorSubject<boolean>(false);
  inventoryModalIsOpen = this.inventoryModalIsOpenSource.asObservable();

  private submitSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  submitSubscriber = this.submitSubscriberSource.asObservable();

  private refreshToolsSubscriberSource = new BehaviorSubject<any>({ next: null, error: null });
  refreshToolsSubscriber = this.refreshToolsSubscriberSource.asObservable();

  defaultInventoryModel: InventoryModel = {
    toolId: null,
    inventoriedDate: null,
    notes: '',
  };

  inventoryModel: InventoryModel = { ...this.defaultInventoryModel };
  inventoryModalSubscription: Subscription;

  constructor(
    private toolsService: ToolsService,
    private toastService: ToastService,
  ) { }

  inventory(item: SelectedDataModel, refreshSubscription: Subscription = undefined) {
    this.inventoryModalSubscription = refreshSubscription;
    this.inventoryModalIsOpenSource.next(true);

    if (item) {
      this.add(item);
    }
  }

  batchInventory(refreshSubscription: Subscription = undefined) {
    this.inventoryModalSubscription = refreshSubscription;
    this.inventoryModalIsOpenSource.next(true);
  }

  batchAdd(items: Array<SelectedDataModel>) {
    const currentList = this.selectedInventoryItemsSource.value;
    let updatedList = [...currentList, ...items];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  batchRemove(items: Array<SelectedDataModel>) {
    const currentList = this.selectedInventoryItemsSource.value;
    const updatedList = this.filterRemovable(currentList, items);
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  add(item: SelectedDataModel, promisify: boolean = false) {
    const itemIsToolInventoryJunction = !!item?.toolInventoryId
    const lookupId = itemIsToolInventoryJunction ? item.toolId : item.id

    if (promisify) {
      return new Promise((resolve, reject) => {
        this.toolsService.readById
          .tools(lookupId, {
            cache: 'clear',
            embed: 'assignment,alert,model',
          })
          .subscribe(
            res => {
              const { data } = res;
              this.remove(data.id);
              const currentList = this.selectedInventoryItemsSource.value;
              let updatedList = [{ ...data, toolInventoryJunctionId: item.id }, ...currentList];
              updatedList = this.filterDuplicates(updatedList);
              this.selectedInventoryItemsSource.next(updatedList);
              this.saveToLocalStorage();
              resolve(true);
            },
            error => reject(error),
          );
      });
    }

    this.toolsService.readById
      .tools(lookupId, {
        cache: 'clear',
        embed: 'assignment,alert,model',
      })
      .subscribe(
        res => {
          const { data } = res;
          this.remove(data.id)
          const currentList = this.selectedInventoryItemsSource.value;
          let updatedList = [{ ...data, toolInventoryJunctionId: item.id }, ...currentList];
          updatedList = this.filterDuplicates(updatedList);
          this.selectedInventoryItemsSource.next(updatedList);
          this.saveToLocalStorage();
        },
        error => console.error('error coming from inventory subscription', error),
      );
  }

  get(id: number) {
    const currentList = this.selectedInventoryItemsSource.value;
    return currentList.find(row => row.id === id);
  }

  getSelectedTools() {
    return this.selectedInventoryItems
  }

  getUpdatedListForRemove(id: number) {
    const currentList = this.selectedInventoryItemsSource.value;
    return currentList.filter(row => row.id !== id);
  }

  remove(id: number) {
    const updatedList = this.getUpdatedListForRemove(id)
    this.selectedInventoryItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  clear() {
    this.clearModelData();
    this.selectedInventoryItemsSource.next([]);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-inventory-items', JSON.stringify(this.selectedInventoryItemsSource.value));
  }

  loadFromLocalStorage() {
    const data = JSON.parse(localStorage.getItem('selected-inventory-items')) || [];
    this.selectedInventoryItemsSource.next(data);
  }

  filterDuplicates(list: Array<SelectedDataModel>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  filterRemovable(a: Array<SelectedDataModel>, b: Array<SelectedDataModel>) {
    return a.filter(e => !b.find(f => f.id === e.id));
  }

  async submitTransfer(transferKey: string) {
    if (!this.getIds().length) {
      return this.toastService.showToast('Select at least one tool first', 'error');
    }

    const batchId = await this.toolsService.generateTransferBatchID();

    const modelData = this.getModelData();

    const requests = modelData.map(payload => {
      const queryString = `batchId=${batchId}`;
      return this.toolsService.create['inventory'](payload, queryString);
    });

    forkJoin(requests).subscribe(
      next => this.handleSuccess(next, transferKey),
      error => this.handleError(error),
    );
  }

  markToolsAsInventoried(action?: 'close' | 'transfer' | string) {
    const modelData = this.getModelData()
    const shouldShowError = !this.getIds().length || !modelData.length

    if (shouldShowError) {
      return this.toastService.showToast('Select at least one tool first', 'error');
    }

    const toolIds = modelData.map(el => el.toolId)
    const { inventoriedDate, notes } = this.inventoryModel

    const payload = {
      inventoriedDate,
      notes,
      toolIds,
    }

    return this.toolsService.create['inventory'](payload).subscribe(
      res => this.handleSuccess(res, action),
      error => this.handleError(error)
    )
  }

  handleSuccess(next, transferKey?: string) {
    this.refreshToolsSubscriberSource.next({ next });
    this.toastService.showToast('Tools inventoried successfully', 'success');
    this.clear();

    if (transferKey === 'close') {
      this.close();
    }

    this.submitSubscriberSource.next({ next });
  }

  handleError(err) {
    this.toastService.showToast('Something went wrong', 'error');
    this.submitSubscriberSource.next({ err });
  }

  addModelData(model: TransferModelData) {
    const { key, value } = model;

    if (key === 'notes') {
      const str = (value as string)?.trim();
      if (!str) return;
      this.inventoryModel[key] = str;
    } else {
      this.inventoryModel[key] = value as any;
    }
  }

  removeModelData(model: TransferModelData) {
    const { key } = model;
    delete this.inventoryModel[key];
  }

  clearModelData() {
    const { inventoriedDate } = this.inventoryModel;
    this.inventoryModel = { ...this.defaultInventoryModel };
    this.inventoryModel.inventoriedDate = inventoriedDate;
  }

  getModelData() {
    return this.getIds().map((id: number) => {
      return {
        ...this.inventoryModel,
        toolId: id,
      };
    });
  }

  getIds() {
    return this.selectedInventoryItemsSource.value.map((item: SelectedDataModel) => item.id);
  }

  close() {
    if (this.inventoryModalSubscription) this.inventoryModalSubscription.unsubscribe();
    this.inventoryModalIsOpenSource.next(false);
  }
}
